import { ChangeEvent, FocusEvent } from 'react';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import { InputAdornment } from '@mui/material';
import {
  ariaDescribedByIds,
  BaseInputTemplateProps,
  examplesId,
  getInputProps,
  labelValue,
  FormContextType,
  RJSFSchema,
  StrictRJSFSchema,
  InputPropsType,
} from '@rjsf/utils';
import NumberStepper from './NumberStepper';

const TYPES_THAT_SHRINK_LABEL = ['date', 'datetime-local', 'file', 'time'];

const decimalPlaces = (n: number) => {
  const s = String(n);
  const dot = s.indexOf('.');
  return dot < 0 ? 0 : s.length - dot - 1;
};

export default function BaseInputTemplate<
  T = object,
  S extends StrictRJSFSchema = RJSFSchema,
  F extends FormContextType = object,
>(props: BaseInputTemplateProps<T, S, F>) {
  const {
    id,
    placeholder,
    required,
    readonly,
    disabled,
    label,
    hideLabel,
    onChange,
    onChangeOverride,
    onBlur,
    onFocus,
    options,
    schema,
    rawErrors = [],
    InputLabelProps,
    ...textFieldProps
  } = props;
  const type = props.type as string;
  const value = props.value as string | undefined | object;

  const schemaTypes = Array.isArray(schema.type) ? schema.type : schema.type ? [schema.type] : [];
  const isNumber = schemaTypes.includes('number') || schemaTypes.includes('integer');
  const stepValue = Number(schema.multipleOf) || 1;
  const stepBy = (direction: number) => {
    const current = typeof value === 'number' ? value : Number(value);
    const base = Number.isFinite(current) ? current : 0;
    const precision = Math.max(decimalPlaces(base), decimalPlaces(stepValue));
    let next = Number((base + direction * stepValue).toFixed(precision));
    if (typeof schema.minimum === 'number') next = Math.max(schema.minimum, next);
    if (typeof schema.maximum === 'number') next = Math.min(schema.maximum, next);
    if (typeof schema.exclusiveMinimum === 'number' && next <= schema.exclusiveMinimum) return;
    if (typeof schema.exclusiveMaximum === 'number' && next >= schema.exclusiveMaximum) return;
    onChange(next);
  };
  const existingInputProps = (textFieldProps as TextFieldProps).InputProps;
  const resolvedInputProps =
    isNumber && !existingInputProps?.endAdornment
      ? {
          ...existingInputProps,
          endAdornment: (
            <InputAdornment position="end">
              <NumberStepper
                disabled={!!disabled || !!readonly}
                onIncrement={() => stepBy(1)}
                onDecrement={() => stepBy(-1)}
              />
            </InputAdornment>
          ),
        }
      : existingInputProps;

  const inputProps: InputPropsType = getInputProps<T, S, F>(schema, type, options);
  // Now we need to pull out the step, min, max into an inner `inputProps` for material-ui
  const { step, min, max, ...rest } = inputProps;
  const otherProps = {
    inputProps: {
      step,
      min,
      max,
      ...(schema.examples ? { list: examplesId<T>(id) } : undefined),
    },
    ...rest,
  };
  const _onChange = ({ target: { value } }: ChangeEvent<HTMLInputElement>) =>
    onChange(value === '' ? options.emptyValue : value);
  const _onBlur = ({ target: { value } }: FocusEvent<HTMLInputElement>) => onBlur(id, value);
  const _onFocus = ({ target: { value } }: FocusEvent<HTMLInputElement>) => onFocus(id, value);
  const hasValue = value !== undefined && value !== null && value !== '';
  const DisplayInputLabelProps: object =
    TYPES_THAT_SHRINK_LABEL.includes(type) || hasValue
      ? ({
          ...InputLabelProps,
          shrink: true,
        } as object)
      : (InputLabelProps as object);

  const inputInsideProps = {
    ...textFieldProps,
    ...inputProps,
    ...otherProps.inputProps,
  };

  return (
    <>
      <TextField
        id={id}
        name={id}
        placeholder={placeholder}
        label={labelValue(label ?? undefined, hideLabel, undefined)}
        required={required}
        disabled={!!disabled || !!readonly}
        {...otherProps}
        inputProps={inputInsideProps}
        value={(value ?? value === 0) ? value : ''}
        error={rawErrors.length > 0}
        onChange={onChangeOverride ?? _onChange}
        onBlur={_onBlur}
        onFocus={_onFocus}
        InputLabelProps={DisplayInputLabelProps}
        {...(textFieldProps as TextFieldProps)}
        InputProps={resolvedInputProps}
        aria-describedby={ariaDescribedByIds<T>(id, !!schema.examples)}
      />
      {Array.isArray(schema.examples) && (
        <datalist id={examplesId(id)}>
          {(schema.examples as string[])
            .concat(schema.default && !schema.examples.includes(schema.default) ? ([schema.default] as string[]) : [])
            .map((example: string) => {
              return <option key={example} value={example} />;
            })}
        </datalist>
      )}
    </>
  );
}
